import { Loader2, ShieldOff } from 'lucide-react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import {
  ADMIN_ACCESS,
  ForcedPasswordChangeScreen,
  useAuth,
  useLogout,
  usePermissions,
} from '@/features/auth';
import type { Permission } from '@/shared/auth/permissions';
import { ErrorState } from '@/shared/components/error-state';

import { ForbiddenPage } from '../../shared/components/errors/forbidden-page';
import { StatusPage } from '../../shared/components/errors/status-page';

function FullPageSpinner({ label }: { label: string }) {
  return (
    <div className="flex min-h-svh items-center justify-center" role="status" aria-live="polite">
      <Loader2 className="text-muted-foreground size-5 animate-spin" aria-hidden />
      <span className="sr-only">{label}</span>
    </div>
  );
}

/**
 * Shown when a signed-in account turns out to lack `admin.access` — e.g. its
 * role was changed by an administrator while this tab was open. Login already
 * rejects such accounts; this covers the session that outlived its access.
 */
function NoAdminAccess() {
  const logout = useLogout();

  return (
    <StatusPage
      icon={ShieldOff}
      code="403"
      title="This account has no access to the admin"
      description="Your account is signed in, but it is not permitted to use the Digital Codex admin. If you believe this is wrong, ask an administrator to review your role."
      actions={
        <Button variant="outline" size="sm" onClick={logout}>
          Sign out
        </Button>
      }
    />
  );
}

/**
 * Gate for everything behind the login screen.
 *
 * Waits for two things before rendering the shell: the session (tokens read
 * back from storage) and the current user from `/admin/me`, which is where the
 * permissions come from. The `restoring` state matters: without it an
 * authenticated user would be bounced to /login for a frame on refresh (§16).
 *
 * A 401 from `/me` is handled by the HTTP client — refresh, or end the session
 * — after which this re-renders unauthenticated and redirects to login.
 */
export function RequireAuth() {
  const { isAuthenticated, isRestoring, user, isUserLoading, userError, refetchUser } = useAuth();
  const { can } = usePermissions();
  const location = useLocation();

  if (isRestoring) {
    return <FullPageSpinner label="Restoring your session…" />;
  }

  if (!isAuthenticated) {
    // Remember the destination so login can return them to it.
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }

  if (isUserLoading) {
    return <FullPageSpinner label="Loading your account…" />;
  }

  if (!user) {
    // `/me` failed for a reason other than an expired session (network, 5xx).
    return (
      <div className="flex min-h-svh items-center justify-center p-6">
        <ErrorState
          title="We couldn't load your account"
          error={userError}
          onRetry={() => void refetchUser()}
        />
      </div>
    );
  }

  if (!can(ADMIN_ACCESS)) {
    return <NoAdminAccess />;
  }

  // ADM-07: the backend refuses every other Admin call until this is done.
  if (user.mustChangePassword) {
    return <ForcedPasswordChangeScreen />;
  }

  return <Outlet />;
}

/**
 * Gate for a route that needs a specific capability, checked against the
 * permissions `/admin/me` returned.
 *
 * Renders a 403 rather than redirecting: silently sending someone elsewhere
 * makes a bookmarked link look broken. Telling them the page exists but is not
 * theirs is both clearer and easier to support. It never signs the user out —
 * a missing permission is not an authentication failure.
 */
export function RequirePermission({
  permission,
}: {
  /** A list means any one of them. */
  permission: Permission | readonly Permission[];
}) {
  const { can, canAny } = usePermissions();

  if (typeof permission === 'string' ? !can(permission) : !canAny(permission)) {
    return <ForbiddenPage />;
  }

  return <Outlet />;
}
